import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { User, ICreateUserRequest, IUpdateUserRequest } from '@pages/authentication/interfaces/user';
import { SwalService } from '@shared/utils/swal.service';
import { UserService } from '@pages/user/services/user.service';
import { RolService } from '@pages/rol/services/rol.service';
import { CryptDataService } from '@shared/utils/crypt-data.service';
import { ParametrosvariablesService } from '@p-parametria/parametros/services/parametrosvariables.service';
import { GestoresService } from '@p-parametria/control-usuarios/gestores/services/gestores.service';
import { EmpresasService } from '@p-parametria/mantenimiento/abc-empresas/services/empresas.service';
import { SolicitudesService } from '@pages/fabrica-credito/services/solicitudes.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.css'],
  providers: [MessageService]
})
export class UserFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() model: User | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  
  form!: FormGroup;
  loading: boolean = false;
  Rols: any[] = [];
  estadosOptions = [
    { label: 'Activo', value: true },
    { label: 'Inactivo', value: false }
  ];
  
  // Opciones para los nuevos campos
  tiposUsuarioOptions: any[] = [];
  idiomasOptions: any[] = [];
  
  // Opciones dinámicas para los campos
  regionalesOptions: any[] = [];
  companiasOptions: any[] = [];
  sucursalesOptions: any[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private _swalService: SwalService,
    private _rolService: RolService,
    private _userServices: UserService,
    private crypto: CryptDataService,
    private _parametrosVariables: ParametrosvariablesService,
    private _gestoresService: GestoresService,
    private _empresasService: EmpresasService,
    private _solicitudesService: SolicitudesService
  ) {}

  displayNivelesDialog: boolean = false;
  userNameError: boolean = false;
  emailError: boolean = false;
  public showAdministradorCheckbox: boolean = false;

  abrirNiveles(): void {
    this.displayNivelesDialog = true;
  }

  cerrarNivelesDialog(): void {
    this.displayNivelesDialog = false;
  }

  // Limpiar errores cuando el usuario modifica los campos
  clearUserNameError(): void {
    this.userNameError = false;
  }

  clearEmailError(): void {
    this.emailError = false;
  }

  ngOnInit(): void {
    this.initForm();
    this.readRol();
    this.loadOpciones();
    this.setupFormDependencies();
    
    // Si ya tenemos un modelo al inicializar, cargarlo
    if (this.model) {
      setTimeout(() => {
        this.loadUserDetail(this.model);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && this.form) {
      if (changes['model'].currentValue) {
        this.loadUserDetail(this.model);
      } else {
        this.form.reset();
        // Habilitar campos cuando se está creando un nuevo usuario
        this.form.get('email')?.enable();
        this.form.get('userName')?.enable();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.form = this.fb.group({
      id: [0], // Campo oculto para el ID del usuario (edición)
      numeroDocumento: [''], // Nuevo campo para número de documento
      rolId: [0, [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      nombre: ['', [Validators.required]],
      password: [''],
      phone: ['', [Validators.required]],
      userName: ['', [Validators.required, Validators.maxLength(6)]], // Máximo 6 caracteres
      active: [true, [Validators.required]],
      userCreated: [JSON.parse(sessionStorage.getItem('Usser'))],
      dateCreated: ['2021-01-01T17:16:40'],
      userModified: [JSON.parse(sessionStorage.getItem('Usser'))],
      // Nuevos campos sin funcionalidad por ahora
      tipoUsuario: [''],
      nivel: [''],
      idioma: [''],
      lineaAutorizada: [0],
      promotorInterno: [''],
      regional: [''],
      compania: [''],
      sucursal: [''],
      administrador: [false],
      autorizaciones: [false]
    });

  }

  loadUserDetail(user: User): void {
    if (this.form && user) {
      this.form.patchValue({
        id: user.id, // ID del usuario para edición
        numeroDocumento: user.numeroDocumento || '', // Usar solo numeroDocumento, sin fallback al id
        rolId: user.rolId,
        email: user.email,
        password: '1234',
        phone: user.phone,
        userName: user.userName,
        active: user.active,
        nombre: user.nombre,
        userCreated: user.userCreated,
        dateCreated: user.dateCreated,
        userModified: JSON.parse(sessionStorage.getItem('Usser')),
        // Nuevos campos con valores por defecto
        tipoUsuario: user.tipoUsuario || '',
        nivel: user.nivel || '',
        idioma: user.idioma || '',
        lineaAutorizada: user.lineaAutorizada || 0,
        promotorInterno: user.promotorInterno || '',
        regional: user.regional || '',
        compania: user.compania || '',
        sucursal: user.sucursal || '',
        administrador: user.administrador || false,
        autorizaciones: user.autorizaciones || false
      });

      // Deshabilitar campos que no se pueden editar
      this.form.get('email')?.disable();
      this.form.get('userName')?.disable();

      // Si hay compañía seleccionada, cargar las sucursales
      if (user.compania) {
        this.loadSucursales(user.compania);
      }
    }
  }

  readRol(): void {
    this._rolService.read()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.Rols = res.data;
        },
        error: (error) => {
          console.error('Error al cargar roles:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los roles'
          });
        }
      });
  }

  loadOpciones(): void {
    // Cargar Tipo de Usuario
    this._parametrosVariables.Getcmspr_opciones('usuarios', 'TipoUsuario')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.tiposUsuarioOptions = res.map((item: any) => ({
            label: item.sOpcion,
            value: item.sValor
          }));
        },
        error: (error) => {
          console.error('Error al cargar tipos de usuario:', error);
        }
      });

    // Cargar Idiomas
    this._parametrosVariables.Getcmspr_opciones('usuarios', 'version')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.idiomasOptions = res.map((item: any) => ({
            label: item.sOpcion,
            value: item.sValor
          }));
        },
        error: (error) => {
          console.error('Error al cargar idiomas:', error);
        }
      });

    // Cargar Regionales usando el mismo servicio que compañía
    this._gestoresService.getEmpresas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.regionalesOptions = res.map((item: any) => ({
            label: `${item.sEmpresa} | ${item.sNombre} |  ${item.responsable} |  ${item.telefono} |  ${item.cuentaContable}`,
            value: item.sEmpresa
          }));
        },
        error: (error) => {
          console.error('Error al cargar regionales:', error);
        }
      });

    // Cargar Empresas usando el servicio de gestores
    this._gestoresService.getEmpresas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.companiasOptions = res.map((item: any) => ({
            label: `${item.sEmpresa} | ${item.sNombre} |  ${item.responsable} |  ${item.telefono} |  ${item.cuentaContable}`,
            value: item.sEmpresa
          }));
        },
        error: (error) => {
          console.error('Error al cargar empresas:', error);
        }
      });
  }

  setupFormDependencies(): void {
    // Suscribirse a cambios en la empresa para cargar sucursales
    this.form.get('compania')?.valueChanges.subscribe(empresa => {
      if (empresa) {
        this.loadSucursales(empresa);
      } else {
        this.sucursalesOptions = [];
        this.form.patchValue({ sucursal: '' });
      }
    });
  }

  loadSucursales(empresa: string): void {
    this._empresasService.GetSucursales(empresa)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.sucursalesOptions = res.data.map((item: any) => ({
            label: `${item.ssucursal} | ${item.nombre_sucursal}`,
            value: item.ssucursal
          }));
        },
        error: (error) => {
          console.error('Error al cargar sucursales:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar las sucursales'
          });
        }
      });
  }

  keyPressNumbers(event) {
    var charCode = (event.which) ? event.which : event.keyCode;
    if ((charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    } else {
      return true;
    }
  }


  async add(payload: any) {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;


    // Mapear datos al nuevo formato del endpoint GetNwUsrs
    const upperUserName = (payload.userName || '').toUpperCase();

    const userData: ICreateUserRequest = {
      rolId: payload.rolId,
      email: payload.email,
      phone: payload.phone,
      userName: upperUserName,
      active: payload.active ? 1 : 0,
      userCreated: payload.userCreated,
      dateCreated: new Date().toISOString().replace('T', ' ').substring(0, 23), // Formato: "2025-06-30 16:00:08.397"
      userModified: null, // Campo vacío como en la documentación
      dateModified: null, // Campo vacío como en la documentación
      password: '',
      passwordE: '',
      passwordAntE: '',
      nombre: payload.nombre,
      numeroDocumento: payload.numeroDocumento, // @NumeroDocumento
      administrador: payload.administrador ? 1 : 0, // @Administrador
      autorizacion: payload.autorizaciones ? 1 : 0, // @Autorizacion
      tipoUsuario: payload.tipoUsuario || '', // @TipoUsuario
      nivel: parseInt(payload.nivel) || 0, // @Nivel
      idioma: parseInt(payload.idioma) || 0, // @idioma
      lineaAutorizada: payload.lineaAutorizada || 0, // @LineaAutorizada
      promotorinterno: payload.promotorInterno || '', // @Promotorinterno
      sEmpresa: payload.compania || '', // @sEmpresa (Compañía)
      sSucursal: payload.sucursal || '', // @sSucursal (Sucursal)
      grupoEmpresa: payload.regional || '' // @GrupoEmpresa (Regional)
    };

    this._userServices.createUserNew(userData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          // Verificar si el usuario se creó exitosamente por la presencia de idUser
          const isSuccess = res.success || (res.data && res.data.length > 0 && res.data[0].idUser);
          
          if (isSuccess) {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: res.data?.[0]?.result || 'Usuario creado correctamente'
            });
            this.onSave.emit({
              success: true,
              message: res.data?.[0]?.result || 'Usuario creado correctamente'
            });
          } else {
            const errorMessage = res.data?.[0]?.result || 'Error al crear usuario';
            
            // Detectar errores específicos y marcar los campos
            if (errorMessage.toLowerCase().includes('usuario ya existe')) {
              this.userNameError = true;
            }
            if (errorMessage.toLowerCase().includes('correo ya existe')) {
              this.emailError = true;
            }
            
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: errorMessage
            });
            this.onSave.emit({
              success: false,
              message: errorMessage
            });
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error al crear usuario:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear usuario'
          });
          this.onSave.emit({
            success: false,
            message: 'Error al crear usuario'
          });
        }
      });
  }

  update(model: User) {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    // Obtener todos los valores del formulario, incluyendo campos deshabilitados
    const formData = this.form.getRawValue();
    
    // Usar el endpoint específico para actualización GetUpdUsrs
    const upperUserName = (formData.userName || '').toUpperCase();

    const userData: IUpdateUserRequest = {
      id: model.id, // ID del usuario para actualización
      rolId: formData.rolId,
      email: formData.email,
      phone: formData.phone,
      userName: upperUserName,
      active: formData.active ? 1 : 0,
      userCreated: model.userCreated,
      dateCreated: model.dateCreated,
      userModified: JSON.parse(sessionStorage.getItem('Usser')),
      dateModified: new Date().toISOString().replace('T', ' ').substring(0, 23),
      nombre: formData.nombre || '',
      numeroDocumento: formData.numeroDocumento || '', // Usar numeroDocumento en lugar de id
      administrador: formData.administrador ? 1 : 0,
      autorizacion: formData.autorizaciones ? 1 : 0,
      tipoUsuario: formData.tipoUsuario || '',
      nivel: parseInt(formData.nivel) || 0,
      idioma: parseInt(formData.idioma) || 0,
      lineaAutorizada: formData.lineaAutorizada || 0,
      promotorinterno: formData.promotorInterno || '',
      sEmpresa: formData.compania || '',
      sSucursal: formData.sucursal || '',
      grupoEmpresa: formData.regional || ''
    };

    this._userServices.updateUserNew(userData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          const isSuccess = res.success || (res.data && res.data.length > 0 && res.data[0].idUser);
          
          if (isSuccess) {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: res.data?.[0]?.result || 'Usuario actualizado correctamente'
            });
            this.onSave.emit({
              success: true,
              message: res.data?.[0]?.result || 'Usuario actualizado correctamente'
            });
          } else {
            const errorMessage = res.data?.[0]?.result || 'Error al actualizar usuario';
            
            // Detectar errores específicos y marcar los campos
            if (errorMessage.toLowerCase().includes('usuario ya existe')) {
              this.userNameError = true;
            }
            if (errorMessage.toLowerCase().includes('correo ya existe')) {
              this.emailError = true;
            }
            
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: errorMessage
            });
            this.onSave.emit({
              success: false,
              message: errorMessage
            });
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error al actualizar usuario:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar el usuario'
          });
          this.onSave.emit({
            success: false,
            message: 'Error al actualizar el usuario'
          });
        }
      });
  }



  //#region Get Fb
  formValue(value: string) {
    return this.form.get(value);
  }
  //#endregion
}
